import React, { useState } from 'react';
import { LinkIcon, ServerIcon, SaveIcon, UploadIcon, RefreshCwIcon, CheckIcon, XIcon } from 'lucide-react';
export function ExternalServices({
  locale
}) {
  // State for external service configuration
  const [externalServices, setExternalServices] = useState({
    licenseTrackingEnabled: false,
    licenseApiUrl: '',
    licenseApiKey: '',
    webhookEnabled: false,
    webhookUrl: '',
    webhookSecret: '',
    lastSyncTime: null,
    syncStatus: 'Not Synced'
  });
  // Handle toggle changes
  const handleToggleChange = e => {
    const {
      name,
      checked
    } = e.target;
    setExternalServices({
      ...externalServices,
      [name]: checked
    });
  };
  // Handle input changes
  const handleInputChange = e => {
    const {
      name,
      value
    } = e.target;
    setExternalServices({
      ...externalServices,
      [name]: value
    });
  };
  // Handle form submission
  const handleSubmit = e => {
    e.preventDefault();
    // Simulate saving the configuration
    setExternalServices({
      ...externalServices,
      lastSyncTime: new Date().toISOString(),
      syncStatus: 'Success'
    });
  };
  // Handle sync now
  const handleSyncNow = () => {
    // Simulate syncing with external service
    setExternalServices({
      ...externalServices,
      lastSyncTime: new Date().toISOString(),
      syncStatus: 'Success'
    });
  };
  // Handle file upload
  const handleFileUpload = e => {
    // Simulate file upload processing
    if (e.target.files && e.target.files[0]) {
      // In a real implementation, you would process the CSV file here
      alert('File uploaded successfully. Data would be processed in a real implementation.');
    }
  };
  return <div className="space-y-6" data-id="element-17603">
      <div data-id="element-17604">
        <h3 className="text-lg font-medium text-gray-900" data-id="element-17605">
          External Usage Services
        </h3>
        <p className="mt-1 text-sm text-gray-500" data-id="element-17606">
          Configure external license tracking and usage reporting services
        </p>
      </div>
      {/* External License Tracking */}
      <div className="bg-white shadow sm:rounded-lg" data-id="element-17607">
        <div className="px-4 py-5 sm:p-6" data-id="element-17608">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-17609">
            External License Tracking
          </h3>
          <div className="mt-2 max-w-xl text-sm text-gray-500" data-id="element-17610">
            <p data-id="element-17611">
              Connect to an external license tracking service to synchronize
              usage data.
            </p>
          </div>
          <form className="mt-5 space-y-6" onSubmit={handleSubmit} data-id="element-17612">
            <div className="flex items-start" data-id="element-17613">
              <div className="flex items-center h-5" data-id="element-17614">
                <input id="licenseTrackingEnabled" name="licenseTrackingEnabled" type="checkbox" checked={externalServices.licenseTrackingEnabled} onChange={handleToggleChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-17615" />
              </div>
              <div className="ml-3 text-sm" data-id="element-17616">
                <label htmlFor="licenseTrackingEnabled" className="font-medium text-gray-700" data-id="element-17617">
                  Enable External License Tracking
                </label>
                <p className="text-gray-500" data-id="element-17618">
                  When enabled, usage data will be synchronized with the
                  external service.
                </p>
              </div>
            </div>
            {externalServices.licenseTrackingEnabled && <div className="space-y-6" data-id="element-17619">
                <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-17620">
                  <div className="sm:col-span-4" data-id="element-17621">
                    <label htmlFor="licenseApiUrl" className="block text-sm font-medium text-gray-700" data-id="element-17622">
                      API URL
                    </label>
                    <div className="mt-1 flex rounded-md shadow-sm" data-id="element-17623">
                      <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm" data-id="element-17624">
                        <LinkIcon className="h-4 w-4" data-id="element-17625" />
                      </span>
                      <input type="url" name="licenseApiUrl" id="licenseApiUrl" value={externalServices.licenseApiUrl} onChange={handleInputChange} className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300" placeholder="https://api.example.com/license" required={externalServices.licenseTrackingEnabled} data-id="element-17626" />
                    </div>
                  </div>
                  <div className="sm:col-span-4" data-id="element-17627">
                    <label htmlFor="licenseApiKey" className="block text-sm font-medium text-gray-700" data-id="element-17628">
                      API Key
                    </label>
                    <div className="mt-1" data-id="element-17629">
                      <input type="password" name="licenseApiKey" id="licenseApiKey" value={externalServices.licenseApiKey} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required={externalServices.licenseTrackingEnabled} data-id="element-17630" />
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-4" data-id="element-17631">
                  <div className="text-sm text-gray-500" data-id="element-17632">
                    {externalServices.lastSyncTime ? <>
                        <span className="font-medium" data-id="element-17633">Last Sync:</span>{' '}
                        {new Date(externalServices.lastSyncTime).toLocaleString(locale)}
                      </> : 'Never synced'}
                  </div>
                  <div data-id="element-17634">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${externalServices.syncStatus === 'Success' ? 'bg-green-100 text-green-800' : externalServices.syncStatus === 'Failed' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-17635">
                      {externalServices.syncStatus === 'Success' ? <>
                          <CheckIcon className="h-3 w-3 mr-1" data-id="element-17636" /> Synced
                        </> : externalServices.syncStatus === 'Failed' ? <>
                          <XIcon className="h-3 w-3 mr-1" data-id="element-17637" /> Failed
                        </> : 'Not Synced'}
                    </span>
                  </div>
                </div>
                <div className="flex justify-end space-x-3" data-id="element-17638">
                  <button type="button" onClick={handleSyncNow} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17639">
                    <RefreshCwIcon className="h-4 w-4 mr-2" data-id="element-17640" />
                    Sync Now
                  </button>
                </div>
              </div>}
            <div className="pt-5 border-t border-gray-200" data-id="element-17641">
              <div className="flex justify-end" data-id="element-17642">
                <button type="submit" className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17643">
                  <SaveIcon className="h-4 w-4 mr-2" data-id="element-17644" />
                  Save Configuration
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
      {/* Webhooks Configuration */}
      <div className="bg-white shadow sm:rounded-lg" data-id="element-17645">
        <div className="px-4 py-5 sm:p-6" data-id="element-17646">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-17647">
            Webhook Integration
          </h3>
          <div className="mt-2 max-w-xl text-sm text-gray-500" data-id="element-17648">
            <p data-id="element-17649">
              Configure webhooks to notify external systems about usage events.
            </p>
          </div>
          <form className="mt-5 space-y-6" onSubmit={handleSubmit} data-id="element-17650">
            <div className="flex items-start" data-id="element-17651">
              <div className="flex items-center h-5" data-id="element-17652">
                <input id="webhookEnabled" name="webhookEnabled" type="checkbox" checked={externalServices.webhookEnabled} onChange={handleToggleChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-17653" />
              </div>
              <div className="ml-3 text-sm" data-id="element-17654">
                <label htmlFor="webhookEnabled" className="font-medium text-gray-700" data-id="element-17655">
                  Enable Webhooks
                </label>
                <p className="text-gray-500" data-id="element-17656">
                  When enabled, usage events will be sent to the specified URL.
                </p>
              </div>
            </div>
            {externalServices.webhookEnabled && <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-17657">
                <div className="sm:col-span-4" data-id="element-17658">
                  <label htmlFor="webhookUrl" className="block text-sm font-medium text-gray-700" data-id="element-17659">
                    Webhook URL
                  </label>
                  <div className="mt-1 flex rounded-md shadow-sm" data-id="element-17660">
                    <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm" data-id="element-17661">
                      <ServerIcon className="h-4 w-4" data-id="element-17662" />
                    </span>
                    <input type="url" name="webhookUrl" id="webhookUrl" value={externalServices.webhookUrl} onChange={handleInputChange} className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300" placeholder="https://hooks.example.com/license" required={externalServices.webhookEnabled} data-id="element-17663" />
                  </div>
                </div>
                <div className="sm:col-span-4" data-id="element-17664">
                  <label htmlFor="webhookSecret" className="block text-sm font-medium text-gray-700" data-id="element-17665">
                    Webhook Secret
                  </label>
                  <div className="mt-1" data-id="element-17666">
                    <input type="password" name="webhookSecret" id="webhookSecret" value={externalServices.webhookSecret} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required={externalServices.webhookEnabled} data-id="element-17667" />
                  </div>
                  <p className="mt-2 text-sm text-gray-500" data-id="element-17668">
                    This secret will be used to sign webhook payloads for
                    verification.
                  </p>
                </div>
              </div>}
            <div className="pt-5 border-t border-gray-200" data-id="element-17669">
              <div className="flex justify-end" data-id="element-17670">
                <button type="submit" className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17671">
                  <SaveIcon className="h-4 w-4 mr-2" data-id="element-17672" />
                  Save Configuration
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
      {/* CSV Upload */}
      <div className="bg-white shadow sm:rounded-lg" data-id="element-17673">
        <div className="px-4 py-5 sm:p-6" data-id="element-17674">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-17675">
            Bulk Import Usage Data
          </h3>
          <div className="mt-2 max-w-xl text-sm text-gray-500" data-id="element-17676">
            <p data-id="element-17677">Upload a CSV file to import usage data in bulk.</p>
          </div>
          <div className="mt-5" data-id="element-17678">
            <div className="max-w-lg flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md" data-id="element-17679">
              <div className="space-y-1 text-center" data-id="element-17680">
                <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true" data-id="element-17681">
                  <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" data-id="element-17682" />
                </svg>
                <div className="flex text-sm text-gray-600" data-id="element-17683">
                  <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500" data-id="element-17684">
                    <span data-id="element-17685">Upload a file</span>
                    <input id="file-upload" name="file-upload" type="file" className="sr-only" accept=".csv" onChange={handleFileUpload} data-id="element-17686" />
                  </label>
                  <p className="pl-1" data-id="element-17687">or drag and drop</p>
                </div>
                <p className="text-xs text-gray-500" data-id="element-17688">CSV up to 10MB</p>
              </div>
            </div>
          </div>
          <div className="mt-5" data-id="element-17689">
            <button type="button" className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17690">
              <UploadIcon className="h-4 w-4 mr-2" data-id="element-17691" />
              Upload CSV
            </button>
          </div>
          <div className="mt-3 text-sm text-gray-500" data-id="element-17692">
            <p data-id="element-17693">
              Download the{' '}
              <a href="#" className="text-indigo-600 hover:text-indigo-500" data-id="element-17694">
                template CSV
              </a>{' '}
              to see the required format.
            </p>
          </div>
        </div>
      </div>
    </div>;
}